import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useSimpleEmpresa } from "@/hooks/useSimpleEmpresa";
// import { useEmpresaApi } from "@/api/multiEmpresaAdapter"; // Migrado a useSimpleEmpresa
import * as vehiculosApiService from "@/services/vehiculosApiService";
import * as clientesApiService from "@/services/clientesApiService";
import * as categoriasApiService from "@/services/categoriasApiService"; 
import Modal from "@/components/common/Modal";
import Button from "@/components/common/Button";
import FilterBar from "@/components/common/FilterBar";
import CustomSelect from "@/components/common/CustomSelect";
import VehiculoList from "./VehiculoList";
import VehiculoForm from "./VehiculoForm";
import HistorialMantenimientos from "./HistorialMantenimientos";
import { FaPlus, FaSync, FaBuilding } from "react-icons/fa";
import { useAuth } from "@/context/AuthContextApi";
import toastService from "@/utils/toastService";

/**
 * Componente principal para la gestión de vehículos con soporte multi-empresa
 * 
 * @returns {JSX.Element} - Componente de mantenedor de vehículos
 */
const VehiculosMantenedor = () => {
  const { clienteId } = useParams(); // Obtén el ID del cliente desde la URL
  const [vehiculos, setVehiculos] = useState([]);
  const [filteredVehiculos, setFilteredVehiculos] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [clientes, setClientes] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [filters, setFilters] = useState({
    categoria: '',
    searchTerm: ''
  });
  const [selectedVehiculo, setSelectedVehiculo] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isHistorialModalOpen, setIsHistorialModalOpen] = useState(false);
  const [selectedPatente, setSelectedPatente] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ totalActivos: 0, nuevosEsteMes: 0 });

  // Contextos y APIs
  const { user, isAdmin, clienteData } = useAuth();
  const { empresaActual, loading: empresaLoading, isSuperAdmin } = useSimpleEmpresa();
  // const empresaApi = useEmpresaApi(); // Ya se usa useSimpleEmpresa arriba

  // Cargar datos iniciales al cambiar empresa - renderizado reactivo
  useEffect(() => {
    if (empresaActual?.id) {
      fetchVehiculos();
      fetchClientes();
      // Stats deshabilitadas (endpoint no implementado)
      // if (isAdmin || isSuperAdmin) {
      //   fetchStats();
      // }
    }
  }, [empresaActual?.id, clienteId, isAdmin, isSuperAdmin]);

  // Cargar categorías de vehículos desde la API
  const fetchCategorias = async () => {
    try {
      const categoriasData = await categoriasApiService.getCategoriasByTipo('tipo_vehiculo');
      setCategorias(categoriasData);
    } catch (error) {
      console.error('Error al cargar categorías de vehículos:', error);
      toastService.error('Error al cargar tipos de vehículo');
    }
  };

  // Cargar categorías al montar el componente
  useEffect(() => {
    fetchCategorias();
  }, []);

  // Aplicar filtros cuando cambien los vehículos o los filtros
  useEffect(() => {
    applyFilters();
  }, [vehiculos, filters]);

  // Función para cargar vehículos desde la API
  const fetchVehiculos = async () => {
    setLoading(true);
    try {

      
      let vehiculosData = [];
      
      if (isSuperAdmin || isAdmin) {
        // Admin/SuperAdmin: cargar todos los vehículos de la empresa o filtrar por cliente
        if (clienteId) {

          vehiculosData = await vehiculosApiService.getVehiculos(empresaActual.id, clienteId);
        } else {

          vehiculosData = await vehiculosApiService.getVehiculos(empresaActual.id);

        }
      } else if (clienteData) {
        // Cliente logueado: cargar solo sus vehículos

        vehiculosData = await vehiculosApiService.getVehiculos(empresaActual.id, clienteData.id);
      } else {

      }


      setVehiculos(vehiculosData);
      setFilteredVehiculos(vehiculosData);
    } catch (error) {
      console.error('❌ Error al cargar vehículos:', error);
      toastService.error('Error al cargar vehículos: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Función para cargar clientes
  const fetchClientes = async () => {
    try {
      if (isSuperAdmin || isAdmin) {
        // Admin/SuperAdmin: cargar todos los clientes de la empresa
        const clientesData = await clientesApiService.getClientes(empresaActual.id);
        setClientes(clientesData);
      } else if (clienteData) {
        // Cliente: solo sus propios datos
        setClientes([clienteData]);
      }
    } catch (error) {
      console.error('Error al cargar clientes:', error);
      toastService.error('Error al cargar clientes');
    }
  };

  // Función para cargar estadísticas - DESHABILITADA (endpoint no existe)
  const fetchStats = async () => {
    // Endpoint /api/vehiculos/stats no implementado - evitar error 404
    console.log('Stats deshabilitadas - endpoint no implementado');
    // try {
    //   const statsData = await vehiculosApiService.getVehiculosStats(empresaActual.id);
    //   setStats(statsData);
    // } catch (error) {
    //   console.error('Error al cargar estadísticas:', error);
    // }
  };

  // Manejar búsqueda de vehículos
  const handleSearch = (value) => {
    setSearchTerm(value);
    const newFilters = { ...filters, searchTerm: value };
    setFilters(newFilters);
  };

  // Manejar cambios en filtros
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    const newFilters = { ...filters, [name]: value };
    setFilters(newFilters);
  };

  // Resetear filtros
  const handleResetFilters = () => {
    setSearchTerm('');
    setFilters({ categoria: '', searchTerm: '' });
  };

  // Aplicar filtros
  const applyFilters = () => {

    
    let filtered = [...vehiculos];
    
    // Filtrar por categoría/tipo de vehículo
    if (filters.categoria) {
      const beforeCategoriaFilter = filtered.length;
      filtered = filtered.filter(v => {
        // Buscar por categoria_id si existe
        if (v.categoria_id && v.categoria_id.toString() === filters.categoria.toString()) {
          return true;
        }
        // Buscar por tipo de vehículo para compatibilidad
        if (v.Tipo === filters.categoria) {
          return true;
        }
        return false;
      });

    }
    
    // Filtrar por término de búsqueda
    if (filters.searchTerm) {
      const beforeSearchFilter = filtered.length;
      const searchLower = filters.searchTerm.toLowerCase();
      const searchClean = searchLower.replace(/[\s-]/g, '');
      const isNumeric = /^\d+$/.test(filters.searchTerm.trim());
      
      filtered = filtered.filter(vehiculo => {
        // Búsqueda por patente
        const patente = vehiculo.patente || vehiculo.Patente || '';
        const patenteClean = patente.replace(/[\s-]/g, '').toLowerCase();
        const patenteMatch = patenteClean.includes(searchClean);
        
        // Búsqueda por modelo
        const modelo = vehiculo.modelo || vehiculo.Modelo || '';
        const modeloMatch = modelo.toLowerCase().includes(searchLower);
        
        // Búsqueda por marca
        const marca = vehiculo.marca || vehiculo.Marca || '';
        const marcaMatch = marca.toLowerCase().includes(searchLower);
        
        // Búsqueda por año
        let anioMatch = false;
        if (isNumeric) {
          const anio = vehiculo.año || vehiculo.Año || '';
          const anioStr = String(anio);
          anioMatch = anioStr.includes(filters.searchTerm.trim());
        }
        
        // Búsqueda por color
        const color = vehiculo.color || vehiculo.Color || '';
        const colorMatch = color.toLowerCase().includes(searchLower);
        
        // Búsqueda por propietario
        const clienteNombre = vehiculo.cliente_nombre || vehiculo.ClienteNombre || '';
        const propietarioMatch = clienteNombre.toLowerCase().includes(searchLower);
        
        return patenteMatch || modeloMatch || marcaMatch || anioMatch || colorMatch || propietarioMatch;
      });

    }
    

    setFilteredVehiculos(filtered);
  };

  // Abrir modal para agregar o editar vehículo
  const handleOpenModal = (vehiculo = null) => {
    setSelectedVehiculo(vehiculo);
    setIsModalOpen(true);
  };

  // Cerrar modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedVehiculo(null);
  };

  // Manejar envío del formulario
  const handleSubmitForm = async (formData) => {
    try {

      
      if (selectedVehiculo) {
        // Actualizar vehículo existente
        await vehiculosApiService.updateVehiculo(selectedVehiculo.patente, formData, empresaActual.id);
        toastService.success("Vehículo actualizado correctamente");
      } else {
        // Crear nuevo vehículo
        await vehiculosApiService.createVehiculo(formData, empresaActual.id);
        toastService.success("Vehículo creado correctamente");
      }
      
      // Recargar la lista de vehículos
      await fetchVehiculos();
      // Stats deshabilitadas (endpoint no implementado)
      // if (isAdmin || isSuperAdmin) {
      //   await fetchStats();
      // }
      
      // Cerrar el modal
      handleCloseModal();
    } catch (error) {
      console.error("Error al guardar vehículo:", error);
      toastService.error(error.message || "Error al guardar vehículo");
    }
  };

  // Manejar eliminación de vehículo
  const handleDeleteVehiculo = async (patente) => {
    if (window.confirm("¿Estás seguro de que deseas eliminar este vehículo?")) {
      try {
        await vehiculosApiService.deleteVehiculo(patente, empresaActual.id);
        await fetchVehiculos();
        // Stats deshabilitadas (endpoint no implementado)
        // if (isAdmin || isSuperAdmin) {
        //   await fetchStats();
        // }
        toastService.success("Vehículo eliminado correctamente");
      } catch (error) {
        console.error("Error al eliminar vehículo:", error);
        toastService.error(error.message || "Error al eliminar vehículo");
      }
    }
  };

  // Manejar visualización del historial de mantenimientos
  const handleViewHistory = (patente) => {
    setSelectedPatente(patente);
    setIsHistorialModalOpen(true);
  };

  // Cerrar modal de historial
  const handleCloseHistorialModal = () => {
    setIsHistorialModalOpen(false);
    setSelectedPatente(null);
  };

  // COMENTADO: Permitir renderizado reactivo sin loading screens
  /*
  // Mostrar loading si la empresa está cargando
  if (empresaLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-300">Cargando empresa...</p>
        </div>
      </div>
    );
  }

  // Mostrar error si no hay empresa seleccionada
  if (!empresaActual?.id) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <FaBuilding className="text-gray-400 text-6xl mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">No hay empresa seleccionada</h2>
          <p className="text-gray-300">Selecciona una empresa para gestionar vehículos</p>
        </div>
      </div>
    );
  }
  */

  // Nombre del cliente si estamos en la vista filtrada
  const clienteFiltrado = clienteId
    ? clientes.find(c => c.id === parseInt(clienteId))
    : null;

  let pageTitle = "Mis Vehículos";
  if (isSuperAdmin || isAdmin) {
    pageTitle = clienteFiltrado 
      ? `Vehículos de ${clienteFiltrado.nombre} ${clienteFiltrado.apellido}` 
      : "Gestión de Vehículos";
  }

  return (
    <div className="p-4 md:p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">{pageTitle}</h1>
        
        <Button
          onClick={() => handleOpenModal()}
          disabled={loading}
          variant="primary"
          icon={<FaPlus />}
        >
          Nuevo Vehículo
        </Button>
      </div>

      {/* Barra de filtros */}
      <FilterBar 
        searchValue={searchTerm}
        onSearch={handleSearch}
        onReset={handleResetFilters}
        searchPlaceholder="Buscar por patente, marca, modelo o propietario..."
        className="mb-6"
      >
        {/* Filtro por tipo de vehículo */}
        <CustomSelect
          name="categoria"
          value={filters.categoria}
          onChange={handleFilterChange}
          className="w-full md:min-w-[200px] md:w-auto bg-gray-700 border border-gray-600 rounded text-white"
          placeholder="Todos los tipos"
          options={categorias.map(cat => ({
            id: cat.id,
            value: cat.id,
            label: cat.nombre
          }))}
        />
      </FilterBar>

      {/* Lista de vehículos */}
      {loading ? (
        <div className="flex justify-center py-6">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <VehiculoList
          vehiculos={filteredVehiculos}
          categorias={categorias}
          onEdit={handleOpenModal}
          onDelete={handleDeleteVehiculo}
          onViewHistory={handleViewHistory}
        />
      )}

      {/* Modal para crear/editar vehículo */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={selectedVehiculo ? "Editar Vehículo" : "Nuevo Vehículo"}
        size="2xl"
      >
        <VehiculoForm
          initialData={selectedVehiculo || {}}
          clientes={clientes}
          categorias={categorias}
          clienteId={!isAdmin && !isSuperAdmin ? clienteData?.id : clienteId}
          isAdmin={isAdmin || isSuperAdmin}
          clienteData={clienteData}
          onSubmit={handleSubmitForm}
          onCancel={handleCloseModal}
        />
      </Modal>

      {/* Modal para historial de mantenimientos */}
      <HistorialMantenimientos
        isOpen={isHistorialModalOpen}
        onClose={handleCloseHistorialModal}
        patente={selectedPatente}
      />
    </div>
  );
};

export default VehiculosMantenedor;